/**
 * VisualElementsPanel - Enhanced Document Visualization
 *
 * Side panel component that displays extracted visual elements (figures, charts, diagrams)
 * from documents with thumbnails, metadata, and navigation capabilities.
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Image,
  BarChart3,
  Table,
  Calculator,
  FileImage,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/service/api'
import type {
  DocumentVisualElements,
  VisualElement,
  VisualElementSummary,
  VisualElementFilter,
  VisualElementTypeConfig,
} from '@/types/visual-elements'

interface VisualElementsPanelProps {
  documentId: string
  isVisible: boolean
  onElementClick?: (pageNumber: number) => void
  onClose?: () => void
  className?: string
  /** Document processing status - used to show appropriate loading messages */
  documentStatus?: string
}

// Visual element type configurations
const ELEMENT_TYPE_CONFIG: Record<string, VisualElementTypeConfig> = {
  figure: {
    icon: Image,
    color: 'gray',
    bgClass: 'bg-white dark:bg-dark-card',
    textClass: 'text-gray-700 dark:text-gray-300',
    borderClass: 'border-gray-200 dark:border-dark-border',
    label: 'Figures',
    description: 'Images and illustrations',
  },
  chart: {
    icon: BarChart3,
    color: 'gray',
    bgClass: 'bg-white dark:bg-dark-card',
    textClass: 'text-gray-700 dark:text-gray-300',
    borderClass: 'border-gray-200 dark:border-dark-border',
    label: 'Charts',
    description: 'Data visualizations',
  },
  table: {
    icon: Table,
    color: 'gray',
    bgClass: 'bg-white dark:bg-dark-card',
    textClass: 'text-gray-700 dark:text-gray-300',
    borderClass: 'border-gray-200 dark:border-dark-border',
    label: 'Tables',
    description: 'Data tables',
  },
  diagram: {
    icon: FileImage,
    color: 'gray',
    bgClass: 'bg-white dark:bg-dark-card',
    textClass: 'text-gray-700 dark:text-gray-300',
    borderClass: 'border-gray-200 dark:border-dark-border',
    label: 'Diagrams',
    description: 'Technical diagrams',
  },
  equation: {
    icon: Calculator,
    color: 'gray',
    bgClass: 'bg-white dark:bg-dark-card',
    textClass: 'text-gray-700 dark:text-gray-300',
    borderClass: 'border-gray-200 dark:border-dark-border',
    label: 'Equations',
    description: 'Mathematical formulas',
  },
}

// Individual visual element card component
interface VisualElementCardProps {
  element: VisualElementSummary
  documentId: string
  onClick?: (pageNumber: number) => void
}

const VisualElementCard: React.FC<VisualElementCardProps> = ({
  element,
  onClick,
}) => {
  const [fullElement, setFullElement] = useState<VisualElement | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const config = ELEMENT_TYPE_CONFIG[element.element_type]
  const IconComponent = config?.icon || Image

  // Load full element details to get semantic summary
  useEffect(() => {
    const loadDetails = async () => {
      setLoadingDetails(true)
      try {
        const details = await apiService.getVisualElementDetails(element.id)
        setFullElement(details)
      } catch (error) {
        console.error(
          `Failed to load details for element ${element.id}:`,
          error
        )
      } finally {
        setLoadingDetails(false)
      }
    }
    loadDetails()
  }, [element.id])

  // Load image through authenticated API and create blob URL
  useEffect(() => {
    let blobUrl: string | null = null

    const loadImage = async () => {
      if (!element.has_image) return

      try {
        const blob = await apiService.getVisualElementImage(element.id, false)
        blobUrl = URL.createObjectURL(blob)
        setImageUrl(blobUrl)
        setImageError(false)
      } catch (error) {
        console.error(`Failed to load image for element ${element.id}:`, error)
        setImageError(true)
      }
    }

    loadImage()

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [element.id, element.has_image])

  const handleClick = () => {
    onClick?.(element.page_number)
  }

  return (
    <div
      className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${config?.bgClass} ${config?.borderClass}`}
      onClick={handleClick}
    >
      {/* Header with type */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <IconComponent className={`w-4 h-4 ${config?.textClass}`} />
          <span className={`text-sm font-medium ${config?.textClass}`}>
            {config?.label}
          </span>
          <Badge variant="secondary" className="text-xs">
            Page {element.page_number}
          </Badge>
        </div>
      </div>

      {/* Image */}
      {element.has_image && (
        <div className="mb-3 bg-white dark:bg-dark-card rounded border overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={
                element.caption ||
                `${config?.label} on page ${element.page_number}`
              }
              className="w-full h-48 object-contain p-2"
            />
          ) : imageError ? (
            <div className="w-full h-48 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Failed to load image
            </div>
          ) : (
            <div className="w-full h-48 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Loading image...
            </div>
          )}
        </div>
      )}

      {/* Caption */}
      {element.caption && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{element.caption}</p>
      )}

      {/* Semantic Summary - show when available, with loading indicator */}
      {loadingDetails &&
        !fullElement?.semantic_summary &&
        element.has_semantic_summary && (
          <div className="mb-2 text-sm text-gray-400 dark:text-gray-500 italic">
            Loading analysis...
          </div>
        )}
      {fullElement?.semantic_summary && (
        <div className="mb-2">
          <p className="text-xs font-medium text-gray-500 dark:text-dark-text-muted mb-1">Analysis:</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {fullElement.semantic_summary}
          </p>
        </div>
      )}
    </div>
  )
}

// Check if a document is still being processed for visual extraction
const isDocumentProcessing = (status?: string): boolean => {
  if (!status) return false
  const processingStatuses = [
    'uploading',
    'indexing_started',
    'indexing',
    'embedding_started',
    'embedding',
    'graph',
    'pnid_processing_started',
    'datasheet_processing_started',
    'timeseries_processing_started',
    'indexing_completed',
    'embedding_completed',
    'processing',
  ]
  return processingStatuses.includes(status)
}

// Main visual elements panel component
export const VisualElementsPanel: React.FC<VisualElementsPanelProps> = ({
  documentId,
  isVisible,
  onElementClick,
  onClose,
  className = '',
  documentStatus,
}) => {
  useTranslation(['data_catalog'])
  const [visualElements, setVisualElements] =
    useState<DocumentVisualElements | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<VisualElementFilter>({
    type: 'all',
    search: '',
    minConfidence: 0,
  })
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['figures', 'charts', 'tables'])
  )

  // Load visual elements when panel becomes visible
  useEffect(() => {
    if (!isVisible || !documentId) {
      return
    }

    const loadVisualElements = async () => {
      // Only show loading spinner on first load
      if (!visualElements) {
        setLoading(true)
      }
      setError(null)

      try {
        const data = await apiService.getDocumentVisualElements(documentId)
        setVisualElements(data)
      } catch (err) {
        console.error('Failed to load visual elements:', err)
        setError('Failed to load visual elements')
      } finally {
        setLoading(false)
      }
    }

    loadVisualElements()

    // Poll for new visual elements while document is still processing
    let pollInterval: NodeJS.Timeout | null = null
    if (isDocumentProcessing(documentStatus)) {
      pollInterval = setInterval(loadVisualElements, 3000) // Poll every 3 seconds for responsive UX
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [isVisible, documentId, documentStatus])

  // Filter and group visual elements
  const filteredElements = useMemo(() => {
    if (!visualElements) return {}

    const applyFilter = (elements: VisualElementSummary[]) => {
      return elements.filter((element) => {
        // Search filter
        if (filter.search && filter.search.length > 0) {
          const searchTerm = filter.search.toLowerCase()
          const caption = element.caption?.toLowerCase() || ''
          if (!caption.includes(searchTerm)) {
            return false
          }
        }

        // Confidence filter
        if (element.confidence_score < (filter.minConfidence || 0)) {
          return false
        }

        return true
      })
    }

    return {
      figures: applyFilter(visualElements.figures),
      charts: applyFilter(visualElements.charts),
      tables: applyFilter(visualElements.tables),
      diagrams: applyFilter(visualElements.diagrams),
      equations: applyFilter(visualElements.equations),
    }
  }, [visualElements, filter])

  const totalFilteredElements = Object.values(filteredElements).reduce(
    (sum, arr) => sum + arr.length,
    0
  )

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const renderElementSection = (
    sectionKey: string,
    elements: VisualElementSummary[],
    title: string,
    icon: React.ComponentType<{ className?: string }>
  ) => {
    if (elements.length === 0) return null

    const IconComponent = icon
    const isExpanded = expandedSections.has(sectionKey)

    return (
      <div key={sectionKey} className="space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-between p-2 h-auto"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-2">
            <IconComponent className="w-4 h-4" />
            <span className="font-medium">{title}</span>
            <Badge variant="secondary" className="text-xs">
              {elements.length}
            </Badge>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </Button>

        {isExpanded && (
          <div className="space-y-2 mt-2">
            {elements.map((element) => (
              <VisualElementCard
                key={element.id}
                element={element}
                documentId={documentId}
                onClick={onElementClick}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (!isVisible) return null

  return (
    <div className={`h-full bg-white dark:bg-dark-card flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 dark:text-dark-text">Visual Elements</h3>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              ×
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <Input
            placeholder="Search captions..."
            value={filter.search}
            onChange={(e) =>
              setFilter((prev) => ({ ...prev, search: e.target.value }))
            }
            className="pl-9"
          />
        </div>

        {/* Summary */}
        {visualElements && (
          <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
            {totalFilteredElements} of {visualElements.total_elements} elements
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Loading state - show when actively loading OR when document is still processing with no elements yet */}
          {(loading ||
            (totalFilteredElements === 0 &&
              isDocumentProcessing(documentStatus))) && (
            <div className="text-center py-8 text-gray-500 dark:text-dark-text-muted">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-pulse">
                  <Image className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <p>Loading visual elements...</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Analyzing document for figures, charts, and diagrams.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-red-500">{error}</div>
          )}

          {/* Only show "No elements found" when document is done processing */}
          {!loading &&
            visualElements &&
            totalFilteredElements === 0 &&
            !isDocumentProcessing(documentStatus) && (
              <div className="text-center py-8 text-gray-500 dark:text-dark-text-muted">
                <div className="flex flex-col items-center gap-2">
                  <Image className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  <p>No visual elements found</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    This document doesn't contain any extractable figures,
                    charts, or diagrams.
                  </p>
                </div>
              </div>
            )}

          {visualElements && totalFilteredElements > 0 && (
            <>
              {renderElementSection(
                'figures',
                filteredElements.figures ?? [],
                'Figures',
                Image
              )}
              {renderElementSection(
                'charts',
                filteredElements.charts ?? [],
                'Charts',
                BarChart3
              )}
              {renderElementSection(
                'tables',
                filteredElements.tables ?? [],
                'Tables',
                Table
              )}
              {renderElementSection(
                'diagrams',
                filteredElements.diagrams ?? [],
                'Diagrams',
                FileImage
              )}
              {renderElementSection(
                'equations',
                filteredElements.equations ?? [],
                'Equations',
                Calculator
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default VisualElementsPanel
